import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../../shared/services';
import { ReportService } from '../../../../../../../../shared/services/report.service';
import { projectDetails, rfisListInfo } from '../../../../../../core/projects.selectors';
import { debounceTime, filter, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { CreateOrEditRfisItemComponent } from '../create-or-edit-rfis-item/create-or-edit-rfis-item.component';
import { FetchRFIsListRequest } from '../../../../../../core/projects.actions';
import { FormControl, FormGroup } from '@angular/forms';
import { RfisService } from '../rfis.service';
import { Report } from '../../../../../../../../shared/models';
import * as moment from 'moment-timezone';
import { Ability, subject } from '@casl/ability';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components';

interface RfiActionRow {
  id: string;
  status: string;
  project?: { id: string };
  createdBy?: { id: string };
  created_by?: string;
}

@Component({
    selector: 'app-rfis-list',
    templateUrl: './rfis-list.component.html',
    styleUrls: ['./rfis-list.component.scss'],
    standalone: false
})
export class RfisListComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput: any;
  @ViewChild('sortSelect') sortSelect: any;
  isSearchFocused = false;
  form = new FormGroup({
    searchText: new FormControl(''),
  });
  defaultDateValue = 'All time';
  displayedColumns: string[] = ['rfiNo', 'title', 'needByDate', 'status', 'createdAt'];
  sortByColumns: string[] = ['RFI no', 'Title', 'Need by date', 'Status', 'Created at'];
  selectedSortColumn: string = 'RFI no';
  sortDirection: 'asc' | 'desc' = 'asc';
  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  originalDataSource: any[] = [];
  statusMap = {
    open: {
      label: 'Awaiting approval',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    submitted: {
      label: 'Awaiting approval',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    approved: {
      label: 'Approved',
      bgColor: 'bg-approved',
      textColor: 'txt-approved',
    },
    draft: {
      label: 'Draft',
      bgColor: 'bg-draft',
      textColor: 'txt-draft',
    },
    sent_for_review: {
      label: 'Sent for review',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    closed: {
      label: 'Closed',
      bgColor: 'bg-approved',
      textColor: 'txt-approved',
    },
  };
  projectId: string;
  selectedRow: any;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;
  account: any;
  role: any;
  canCreate = false;
  startDate = null;
  endDate = null;
  statusIdsToFilter: any[] = [];
  searchText = '';
  projectTimezone: string;
  statusList = [
    {
      label: 'Open',
      value: 'submitted',
      isChecked: false,
    },
    {
      label: 'Closed',
      value: 'closed',
      isChecked: false,
    },
    {
      label: 'Awaiting Submission',
      value: 'draft',
      isChecked: false,
    },
  ];
  deletingRfiId: string = null;
  downloadingRfiId: string = null;
  downloading = false;
  rfiReport: Report = null;
  /**
   * Deep link from global search: `?rifId=` (existing param name, misspelling
   * and all — it's what rowClicked writes and the right panel reads) auto-opens
   * the RFI details panel on load, one-shot, the same way a row click would.
   */
  private pendingDeepLinkId: string = null;
  /** False until the store's synchronous replay of the previous list state
   * has passed; deep-link consumption only trusts fresh emissions. */
  private listReady = false;
  private readonly onDestroy = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    private rfisService: RfisService,
    private reportService: ReportService,
    private snackBar: MatSnackBar,
    private ability: Ability,
  ) {
  }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.canCreate = this.ability.can('create', subject('RFI', { project_id: this.projectId }));
      this.account = this.authService.getCurrentUser();
      this.role = this.account.roles.find(obj => obj.project_id === this.projectId)?.role || this.account.role;

      if (this.role === 'project-manager' || this.role === 'org-admin' || this.role === 'reviewer') {
        if (!this.displayedColumns.includes('latestActivity')) {
          this.displayedColumns.push('latestActivity');
        }
        if (!this.sortByColumns.includes('Latest activity')) {
          this.sortByColumns.push('Latest activity');
        }
      } else {
        this.displayedColumns = this.displayedColumns.filter(
          column => column !== 'latestActivity',
        );
        this.sortByColumns = this.sortByColumns.filter(c => c !== 'Latest activity');
      }
    }

    this.displayedColumns.push('actions');
    this.pendingDeepLinkId = this.route.snapshot.queryParamMap.get('rifId');

    this.fetchRFIsList();
    this.loadRfiReport();

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.startDate && moment(qp.startDate).isValid()) {
          this.startDate = qp.startDate;
        } else {
          this.startDate = null;
        }

        if (qp.endDate && moment(qp.endDate).isValid()) {
          this.endDate = qp.endDate;
        } else {
          this.endDate = null;
        }

        if (qp.status) {
          if (Array.isArray(qp.status)) {
            this.statusIdsToFilter = qp.status;
          } else {
            this.statusIdsToFilter = [qp.status];
          }
        } else {
          this.statusIdsToFilter = [];
        }

        this.applyFilters();
      });

    this.form.controls.searchText.valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.searchText = data || '';
        this.applyFilters();
      });

    this.store
      .select(rfisListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          const list = data?.list ?? [];
          this.originalDataSource = Array.isArray(list) ? list : [];
          this.totalRecords = data?.totalRecords ?? this.originalDataSource.length;

          // Each emission deep-clones the rows, so re-point the reference the
          // highlighted-row styling compares against (`row === selectedRow`).
          if (this.selectedRow?.id) {
            const current = this.originalDataSource.find(row => row?.id === this.selectedRow.id);
            if (current) {
              this.selectedRow = current;
            }
          }

          this.applyFilters();
          // Skip the synchronous replay of the previous store state (possibly
          // another project's list): only fresh fetch responses may consume
          // the deep link — otherwise the id "misses" and falls back to a
          // fetch-by-id without ever surfacing the row in the list.
          if (this.listReady) {
            this.tryOpenDeepLinkedRfi();
          }
        }
      });
    this.listReady = true;

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.timezone) {
          this.projectTimezone = data.timezone;
        }
      });
  }


  fetchRFIsList(): void {
    const payload = {
      qp: {
        start: this.pagination.pageIndex * this.pagination.pageSize,
        total: 1000,
        projectId: this.projectId,
      },
    };

    if (this.projectId) {
      this.store.dispatch(FetchRFIsListRequest({ payload }));
    }
  }

  /**
   * Auto-opens the details panel for a deep-linked RFI (?rifId=) once the list
   * has loaded, reusing the row-click data path. If the RFI isn't in the
   * fetched list (e.g. beyond the fetch window), it is fetched by id instead;
   * an unknown/404 id no-ops silently.
   */
  private tryOpenDeepLinkedRfi(): void {
    if (!this.pendingDeepLinkId) {
      return;
    }
    const id = this.pendingDeepLinkId;
    this.pendingDeepLinkId = null;

    const match = this.originalDataSource.find(row => `${row?.id}` === `${id}`);
    if (match) {
      this.rowClicked(match);
      this.revealDeepLinkedRow(match);
      return;
    }

    this.projectsService.fetchRFIItemDetailsWithId({ rifId: id })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: resp => {
          const record = resp?.data;
          if (record?.id) {
            this.rowClicked(record);
          }
        },
        error: () => {
          // Deep-linked RFI no longer exists (or is not accessible) — stay on the log.
        },
      });
  }

  /**
   * Surfaces the deep-linked row in the left list: clears any blocking
   * status/date filters, jumps to the row's page and scrolls it into view
   * (the highlighted-row styling comes from rowClicked's selectedRow).
   * Timers let rowClicked's queryParams merge (whose applyFilters pass resets
   * pagination) settle first.
   */
  private revealDeepLinkedRow(row): void {
    setTimeout(() => {
      const blocked = this.filteredDataSource.indexOf(row) < 0
        && !!(this.statusIdsToFilter?.length || this.startDate || this.endDate);
      const nav = blocked
        ? this.router.navigate([], {
          queryParams: { status: null, startDate: null, endDate: null },
          queryParamsHandling: 'merge',
        })
        : Promise.resolve(true);

      nav.then(() => setTimeout(() => {
        const index = this.filteredDataSource.indexOf(row);
        if (index < 0) {
          return;
        }
        this.currentPage = Math.floor(index / this.itemsPerPage) + 1;
        this.pagination.pageIndex = this.currentPage - 1;
        this.paginateData();
        setTimeout(() => {
          document.querySelector('app-rfis-list .highlighted-row')
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
      }));
    });
  }

  rowClicked(row: any): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: CreateOrEditRfisItemComponent,
      data: { ...row },
    });

    this.router.navigate([], {
      queryParams: {
        subTab: 'details',
        rifId: row.id,
      },
      queryParamsHandling: 'merge',
    });
  }

  canDeleteRfi(row: RfiActionRow): boolean {
    return row?.status === 'draft' && this.ability.can('delete', subject('RFI', {
      ...row,
      project_id: row.project?.id || this.projectId,
      created_by: row.createdBy?.id || row.created_by,
    }));
  }

  deleteRfi(row: RfiActionRow, event: Event): void {
    event.stopPropagation();
    if (!this.canDeleteRfi(row) || this.deletingRfiId) {
      return;
    }
    this.deletingRfiId = row.id;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '460px',
      data: new ConfirmDialogModel(
        'Delete draft RFI',
        'Are you sure you want to delete this draft RFI? This cannot be undone.',
        'Delete',
        'Cancel',
      ),
    });
    dialogRef.afterClosed().pipe(
      filter(confirmed => confirmed === true),
      switchMap(() => this.projectsService.deleteRFIItem({ id: row.id })),
      takeUntil(this.onDestroy),
      finalize(() => this.deletingRfiId = null),
    ).subscribe({
      next: () => {
        if (this.selectedRow?.id === row.id) {
          this.selectedRow = null;
          this.projectsService.closeRightPanel.next();
          this.router.navigate([], {
            queryParams: { rifId: null },
            queryParamsHandling: 'merge',
          });
        }
        this.fetchRFIsList();
        this.snackBar.open('Draft RFI deleted', '', { duration: 3000 });
      },
      error: () => {
        this.snackBar.open(
          'Unable to delete RFI. It may no longer be a draft, or you may not have permission.',
          '',
          { duration: 5000 },
        );
        this.fetchRFIsList();
      },
    });
  }

  create(): void {
    this.router.navigate([], {
      queryParams: { rifId: null },
      queryParamsHandling: 'merge',
    });

    this.projectsService.openRightPanel.next({
      component: CreateOrEditRfisItemComponent,
      data: {},
    });
  }

  onPageChanged(page: number): void {
    this.projectsService.closeRightPanel.next();
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  onSearchFocus() {
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    this.isSearchFocused = true;
  }

  search() {
    const { searchText } = this.form.getRawValue();
    this.rfisService.search.next(searchText);
  }

  close() {
    this.form.reset();
    const searchText = '';
    this.isSearchFocused = false;
    this.rfisService.search.next(searchText);
  }

  applyFilters() {
    let filteredData = [...this.originalDataSource];

    if (this.startDate) {
      const start = moment(this.startDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data =>
        moment(data.need_by_date, 'YYYY-MM-DD').isSameOrAfter(start),
      );
    }

    if (this.endDate) {
      const end = moment(this.endDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data =>
        moment(data.need_by_date, 'YYYY-MM-DD').isSameOrBefore(end),
      );
    }

    if (this.statusIdsToFilter?.length) {
      filteredData = filteredData.filter(data => {
        if (this.statusIdsToFilter.includes('submitted') && data.status === 'sent_for_review') {
          return true;
        }
        return this.statusIdsToFilter.includes(data.status);
      });
    }

    if (this.searchText.trim()) {
      const search = this.searchText.trim().toLowerCase();
      filteredData = filteredData.filter(data =>
        (
          data.description?.toLowerCase().includes(search) ||
          data.title?.toLowerCase().includes(search) ||
          data.submittal_no?.toLowerCase().includes(search)
        ),
      );
    }

    filteredData = this.sortItems(filteredData);

    this.filteredDataSource = filteredData;
    this.totalRecords = filteredData.length;
    this.paginateData(true);
  }

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    this.sortSelect?.open?.();
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event.value;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.selectedSortColumn = null;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFilters();
  }

  private sortItems(items: any[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const itemsToSort = [...items];

    const safeLower = (v: any) => String(v ?? '').toLowerCase();
    const safeDate = (v: any) => {
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    const naturalSortRfiNo = (a: any, b: any): number => {
      if (typeof a.rfi_no === 'number' && typeof b.rfi_no === 'number') {
        return a.rfi_no - b.rfi_no;
      }

      const aVal = String(a.rfi_no ?? '');
      const bVal = String(b.rfi_no ?? '');

      const parseRfiNo = (val: string): number => {
        const numMatch = val.match(/^\d+$/);
        if (numMatch) {
          return parseInt(val, 10);
        }
        const match = val.match(/(\d+)/);
        if (match) {
          return parseInt(match[1], 10);
        }
        return 0;
      };

      const aNum = parseRfiNo(aVal);
      const bNum = parseRfiNo(bVal);

      if (aNum !== bNum) {
        return aNum - bNum;
      }
      return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
    };

    if (!this.selectedSortColumn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const sortFns: Record<string, (a: any, b: any) => number> = {
      'RFI no': naturalSortRfiNo,
      'Title': (a, b) => safeLower(a.title).localeCompare(safeLower(b.title)),
      'Need by date': (a, b) => safeDate(a.need_by_date) - safeDate(b.need_by_date),
      'Status': (a, b) => safeLower(a.status).localeCompare(safeLower(b.status)),
      'Created at': (a, b) => safeDate(a.created_at) - safeDate(b.created_at),
      'Latest activity': (a, b) => safeDate(a?.latest_lineage?.created_at) - safeDate(b?.latest_lineage?.created_at),
    };

    const fn = sortFns[this.selectedSortColumn];
    if (!fn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const applyDirection = (result: number) => (this.sortDirection === 'asc' ? result : -result);

    return itemsToSort.sort((a, b) => {
      const primary = fn(a, b);
      if (primary !== 0) {
        return applyDirection(primary);
      }
      return safeDate(b.updated_at) - safeDate(a.updated_at);
    });
  }

  formatInProjectTimezone(timestamp: string, format: string): string {
    if (!timestamp) return '';
    const tz = this.projectTimezone || 'UTC';
    return moment.utc(timestamp).tz(tz).format(format);
  }

  async loadRfiReport(): Promise<void> {
    try {
      const reports = await this.reportService.getReports(
        { id: this.projectId } as any,
        null,
        {}
      );
      this.rfiReport = reports.find(r => r.name === 'RFI summary report') || null;
    } catch {
    }
  }

  async downloadRfiReport(): Promise<void> {
    if (!this.rfiReport || this.downloading) {
      return;
    }

    this.downloading = true;

    try {
      const params: any = {
        projectId: this.projectId,
      };

      if (this.statusIdsToFilter && this.statusIdsToFilter.length > 0) {
        // 'submitted' (Open) also includes 'sent_for_review' to match list filter behavior
        const expandedStatuses = [...this.statusIdsToFilter];
        if (expandedStatuses.includes('submitted') && !expandedStatuses.includes('sent_for_review')) {
          expandedStatuses.push('sent_for_review');
        }
        params.entityStatus = expandedStatuses;
      }

      if (this.startDate) {
        params.startDate = moment(this.startDate).format('YYYY-MM-DD');
      }

      if (this.endDate) {
        params.endDate = moment(this.endDate).format('YYYY-MM-DD');
      }

      await this.reportService.downloadReport(
        this.rfiReport,
        null,
        { id: this.projectId },
        params,
        false,
        false
      );

      this.snackBar.open('Report downloaded successfully', '', { duration: 3000 });
    } catch {
      this.snackBar.open('Failed to download report', '', { duration: 5000 });
    } finally {
      this.downloading = false;
    }
  }

  async downloadRfiDetail(row: RfiActionRow, includeDiscussions: boolean, event: Event): Promise<void> {
    event.stopPropagation();
    if (this.downloadingRfiId || !row?.id || !this.projectId) {
      return;
    }
    this.downloadingRfiId = row.id;
    try {
      const report = await this.reportService.resolveEntityReport(this.projectId, 'rfi_detail');
      if (!report) {
        this.snackBar.open(
          'No RFI detail report template is configured. Please contact your administrator.',
          '',
          { duration: 5000 },
        );
        return;
      }
      await this.reportService.downloadReport(
        report,
        null,
        { id: this.projectId },
        {
          rfiId: row.id,
          includeDiscussions,
          attachments: true,
        },
        true,
        false,
      );
      this.snackBar.open('Report downloaded successfully', '', { duration: 3000 });
    } catch {
      this.snackBar.open('Failed to download report', '', { duration: 5000 });
    } finally {
      this.downloadingRfiId = null;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
